/**
 * Утилиты для работы с временем и таймзонами
 * Обеспечивает корректное отображение времени в локальной таймзоне пользователя
 */

/**
 * Конвертирует локальное время в UTC для сохранения в базе данных
 * @param hour - Час (1-12)
 * @param minute - Минуты (0-59)
 * @param ampm - AM/PM
 * @returns ISO строка в UTC
 */
export function convertLocalTimeToUTC(
  hour: string,
  minute: string,
  ampm: string,
): string {
  let hour24 = parseInt(hour, 10);

  // Конвертируем в 24-часовой формат
  if (ampm === "PM" && hour24 !== 12) {
    hour24 += 12;
  }
  if (ampm === "AM" && hour24 === 12) {
    hour24 = 0;
  }

  // Создаем дату в локальной таймзоне
  const now = new Date();
  const localDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hour24,
    parseInt(minute, 10),
  );

  // Проверяем, если время в прошлом, добавляем +1 день
  if (localDate.getTime() < now.getTime()) {
    localDate.setDate(localDate.getDate() + 1);
  }

  // Возвращаем ISO строку (автоматически в UTC)
  return localDate.toISOString();
}

/**
 * Проверяет, является ли время в прошлом
 * @param date - Дата для проверки
 * @returns true если время в прошлом
 */
export function isTimeInPast(date: Date | any): boolean {
  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (date && typeof date.toDate === "function") {
    dateObj = date.toDate();
  } else if (typeof date === "string" || typeof date === "number") {
    dateObj = new Date(date);
  } else {
    return false;
  }

  return dateObj.getTime() < Date.now();
}

/**
 * Проверяет, активна ли встреча (использует openDoorAt время)
 * @param openDoorAt - Время открытия дверей встречи (за 5 минут до начала)
 * @param currentTime - Текущее время (опционально, по умолчанию Date.now())
 * @returns true если встреча активна
 */
export function isRoomActive(
  openDoorAt: Date | any,
  currentTime?: number,
): boolean {
  let dateObj: Date;

  if (openDoorAt instanceof Date) {
    dateObj = openDoorAt;
  } else if (openDoorAt && typeof openDoorAt.toDate === "function") {
    // Firebase Timestamp
    dateObj = openDoorAt.toDate();
  } else if (typeof openDoorAt === "string" || typeof openDoorAt === "number") {
    dateObj = new Date(openDoorAt);
  } else {
    return false;
  }

  // Проверяем валидность даты
  if (isNaN(dateObj.getTime())) {
    return false;
  }

  // Используем переданное время или текущее время
  const now = currentTime ?? Date.now();

  // Встреча активна когда наступило время открытия дверей
  return dateObj.getTime() <= now;
}

const toDate = (date: Date | any): Date | null => {
  if (date instanceof Date) return date;
  if (date && typeof date.toDate === "function") return date.toDate();
  if (typeof date === "string" || typeof date === "number") {
    return new Date(date);
  }
  return null;
};

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * Время начала комнаты для карточки: "Today, 3:00 PM", "Tomorrow, 9:30 AM",
 * "Thu, Oct 16, 3:00 PM". Всегда в местном времени устройства, поэтому без
 * подписи таймзоны: "GMT+5" / "GMT" / пустота на разных устройствах только
 * путали (#142).
 */
export function formatRoomStart(
  date: Date | any,
  now: Date = new Date(),
): string {
  const dateObj = toDate(date);
  if (!dateObj || isNaN(dateObj.getTime())) return "Invalid date";

  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const dayDiff = Math.round(
    (startOfDay(dateObj) - startOfDay(now)) / (24 * 60 * 60 * 1000),
  );
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Tomorrow, ${time}`;
  if (dayDiff === -1) return `Yesterday, ${time}`;

  const day = dateObj.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(dateObj.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  });
  return `${day}, ${time}`;
}

/**
 * Короткая подсказка, когда начнётся комната: "Starts in 10 min",
 * "Starts in 3 h", "Starting now", "Started 5 min ago". Заменяет непонятные
 * "in 10m" / "5m ago" (#142). null, если комната дальше суток — тогда хватает
 * даты из formatRoomStart.
 */
export function getStartsInLabel(
  date: Date | any,
  now: Date = new Date(),
): string | null {
  const dateObj = toDate(date);
  if (!dateObj || isNaN(dateObj.getTime())) return null;

  const diffMinutes = Math.round((dateObj.getTime() - now.getTime()) / 60000);
  if (diffMinutes === 0) return "Starting now";
  if (diffMinutes < 0) {
    const ago = -diffMinutes;
    return ago < 60 ? `Started ${ago} min ago` : null;
  }
  if (diffMinutes < 60) return `Starts in ${diffMinutes} min`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `Starts in ${diffHours} h`;
  return null;
}
